import React from "react";
import { DatePicker } from "antd";
import enUS from "antd/es/date-picker/locale/en_US";
import itIT from "antd/es/date-picker/locale/it_IT";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { cn } from "../../utils/classNames";

const { RangePicker } = DatePicker;

const DATE_FORMATS_BY_LOCALE = {
  en: "YYYY-MM-DD",
  it: "DD/MM/YYYY",
  fr: "DD/MM/YYYY",
  de: "DD.MM.YYYY",
};

const DEFAULT_MIN_DATE = "2017-01-01";

const getDateFormatByLocale = (locale?: string) => {
  if (!locale) return DATE_FORMATS_BY_LOCALE.en;

  const language = locale.split("-")[0];
  return DATE_FORMATS_BY_LOCALE[language as keyof typeof DATE_FORMATS_BY_LOCALE] || DATE_FORMATS_BY_LOCALE.en;
};

const DATE_PICKER_LOCALES = {
  en: enUS,
  it: itIT,
};

const getDatePickerLocale = (locale?: string) => {
  const language = locale?.split("-")[0] ?? "en";
  return (
    DATE_PICKER_LOCALES[language as keyof typeof DATE_PICKER_LOCALES] ??
    DATE_PICKER_LOCALES.en
  );
};

const getPresetLabel = (t: any, key: string, fallback: string) =>
  typeof t === "function" ? t(key, { defaultValue: fallback }) : fallback;

const getRangePickerPreset = (t: any, key: string, fallback: string, value: any) => ({
  label: getPresetLabel(t, key, fallback),
  value,
});

export const getRangePickerPresetsConstant = (t: any) => {
  const yesterday = getRangePickerPreset(t, "yesterday", "Yesterday", [
    dayjs().add(-1, "d"),
    dayjs().add(-1, "d"),
  ]);
  const today = getRangePickerPreset(t, "today", "Today", [dayjs(), dayjs()]);
  const last7Days = getRangePickerPreset(t, "last7Days", "Last 7 Days", [
    dayjs().add(-7, "d"),
    dayjs(),
  ]);
  const last14Days = getRangePickerPreset(t, "last14Days", "Last 14 Days", [
    dayjs().add(-14, "d"),
    dayjs(),
  ]);
  const last30Days = getRangePickerPreset(t, "last30Days", "Last 30 Days", [
    dayjs().add(-30, "d"),
    dayjs(),
  ]);
  const last90Days = getRangePickerPreset(t, "last90Days", "Last 90 Days", [
    dayjs().add(-90, "d"),
    dayjs(),
  ]);

  return {
    default: [
      yesterday,
      today,
      last7Days,
      last14Days,
      last30Days,
      last90Days,
    ],
    yesterday,
    today,
    last7Days,
    last14Days,
    last30Days,
    last90Days,
  };
};

const CustomRangePicker = (props: any) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.resolvedLanguage ?? i18n.language;

  const {
    className,
    disabledDate: originalDisabledDate,
    maxDate = dayjs(),
    minDate = dayjs(DEFAULT_MIN_DATE),
    presets,
    ...restProps
  } = props;

  const defaultDisabledDate = (current: any) =>
    (current && current.isBefore(minDate, "day")) ||
    (current && current.isAfter(maxDate, "day"));

  const finalPresets = presets ?? getRangePickerPresetsConstant(t).default;

  return (
    <RangePicker
      key={currentLang}
      className={cn("custom-range-picker", className)}
      disabledDate={originalDisabledDate || defaultDisabledDate}
      locale={getDatePickerLocale(currentLang)}
      presets={finalPresets}
      placeholder={[
        t("start_date", { defaultValue: "Start date" }),
        t("end_date", { defaultValue: "End Date" }),
      ]}
      {...restProps}
      format={getDateFormatByLocale(currentLang)}
    />
  );
};

export default CustomRangePicker;
